import axios, { type AxiosInstance } from 'axios';
import { ROUTE_BASE } from '~/assets/js/consts/apiRoutes.consts.js';
import show_api_message from '~/assets/js/helpers/show_api_message';
import make_bearer_token from '~/assets/js/functions/make_bearer_token.js';
import { is_response_ok } from '~/assets/js/network/helpers.network.js';
import to_latin_numbers from '~/assets/js/translators/to_latin_numbers.translators.js';
import useToast from '~/assets/js/composables/layout/useToast.js';
import useAuth from '~/assets/js/composables/other/useAuth.js';
import { useState } from 'nuxt/app';

/**
 * A Nuxt 3 composable to create and manage a singleton Axios instance
 * that is safe for Server-Side Rendering (SSR).
 *
 * It replicates the original logic, but wraps it in a composable to ensure
 * it only runs within a valid Nuxt context.
 */
export const useApiClient = () => {
  // Use useState to create a single instance per request on the server,
  // and a single instance for the lifetime of the app on the client.
  const instance = useState<AxiosInstance | null>('axios-instance', () => null);

  // If the instance doesn't exist, create it.
  if (!instance.value) {
    // --- All dependent composables are called safely inside this block ---
    const auth = useAuth();
    const toast = useToast();
    const initialToken = auth.getAuthToken() ?? '';

    // Create the Axios instance with the initial configuration
    const axiosInstance = axios.create({
      baseURL: ROUTE_BASE,
      headers: {
        ...(initialToken && { Authorization: make_bearer_token(initialToken) }),
        'X-Currency': 'IRT',
      },
    });

    // --- Request Interceptor (Exact same logic) ---
    axiosInstance.interceptors.request.use(
      config => {
        if (config?.data) {
          for (const property in config.data) {
            if (typeof config.data[property] === 'string')
              config.data[property] = to_latin_numbers(config.data[property]);
          }
        }
        if (config?.url) {
          config.url = to_latin_numbers(config.url);
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // --- Response Interceptor (Exact same logic) ---
    axiosInstance.interceptors.response.use(
      response => {
        if (!response.config?.__silent) {
          show_api_message(response);
        }
        if (!is_response_ok(response)) throw response;
        return response;
      },
      async error => {
        const response = error.response;
        const error_code = error.toJSON().code;

        // Calls to other composables are now safe
        if (error_code === 'ECONNABORTED') {
          toast.create_danger('پاسخی از سمت سرور دریافت نشد.');
        } else if (error_code === 'ERR_NETWORK') {
          toast.create_danger('ارتباط با سرور برقرار نشد؛ اتصال اینترنت خود را بررسی کنید.');
        } else if (response && !response.config?.__silent) {
          show_api_message(response);
        }

        if (response?.status === 401) {
          auth.make_logout();
        }

        throw error;
      }
    );

    // Store the created instance in the state
    instance.value = axiosInstance;
  }

  /**
   * Updates the Authorization header on the existing Axios instance.
   * This does NOT re-create the instance.
   * @param {string} token - The new authentication token.
   */
  const setToken = (token: string) => {
    if (instance.value) {
      instance.value.defaults.headers.common['Authorization'] = make_bearer_token(token);
    }
  };

  return {
    axiosClient: instance.value,
    setToken,
  };
};
